<template>
    <div class="nav-actions">
        <el-popover placement="bottom-end" trigger="hover" popper-class="video-pop" :show-arrow="false" :popper-style="{
            width: 'auto',
            background: 'rgba(0, 0, 0, 0.2)',
            borderRadius: '20px',
            padding: '24px',
            backdropFilter: 'blur(6px)',
            border: '1px solid rgba(255, 255, 255, 0.10)',
            boxShadow:
                '0 10px 15px -3px rgba(0, 0, 0, 0.10), 0 4px 6px -4px rgba(0, 0, 0, 0.10)',
        }">
            <template #reference>
                <button class="!hidden language-button justify-center items-center">
                    {{ getCurrentLanguageName() }}
                    <svg class="w-4 h-4 ml-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd"
                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                            clip-rule="evenodd" />
                    </svg>
                </button>
            </template>
            <div class="grid grid-cols-2 gap-x-1 min-w-[320px] p-2 gap-1">
                <button v-for="lang in availableLanguages" :key="lang.code"
                    class="px-3 py-2 text-sm border border-transparent rounded-[10px] text-white hover:text-text-active hover:border-[#99f7684d] transition-all duration-300 flex items-center justify-start whitespace-nowrap"
                    :class="{
                        '!text-text-active  !border-[#99f7684d]':
                            locale === lang.code,
                    }" @click="handleLanguageChange(lang.code)">
                    <span>{{ lang.name }}</span>
                </button>
            </div>
        </el-popover>

        <!-- 桌面端显示完整导航，移动端只保留登录 + 语言 + 菜单按钮 -->
        <template v-if="!isLoggedIn && LOGIN_ENTRY_ENABLED">
            <div aria-label="User Login Button" tabindex="0" role="button" class="login-btn-custom"
                @click="() => showLoginModal()">
                {{ t('menus.login') }}
            </div>
        </template>

        <template v-else>
            <div class="flex items-center gap-2">
                <div class="flex items-center coin" @click="emit('coin-click')">
                    <img src="~/assets/images/common/coin.webp" alt="credit" class="w-4 h-4 mr-1" />
                    <span class="text-white text-xs">
                        {{ balance }}
                    </span>
                </div>
                <div ref="dropdownRef" class="relative" @mouseenter="openDropdown" @mouseleave="closeDropdown">
                    <div class="flex items-center gap-1 cursor-pointer p-1 transition-colors duration-200">
                        <img v-if="userInfo?.avatar" :src="userInfo?.avatar" alt="user-avatar"
                            class="w-8 h-8 rounded-full" />
                        <div class="text-sm text-white max-w-[100px] truncate">
                            {{ userInfo?.nick_name }}
                        </div>
                        <svg class="w-4 h-4 text-white transition-transform duration-200 ease-in-out"
                            :class="{ 'rotate-180': isDropdownOpen }" fill="none" stroke="currentColor"
                            viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7">
                            </path>
                        </svg>
                    </div>

                    <Transition enter-active-class="transition-all duration-200 ease-out"
                        enter-from-class="opacity-0 transform -translate-y-2 scale-95"
                        enter-to-class="opacity-100 transform translate-y-0 scale-100"
                        leave-active-class="transition-all duration-150 ease-in"
                        leave-from-class="opacity-100 transform translate-y-0 scale-100"
                        leave-to-class="opacity-0 transform -translate-y-2 scale-95">
                        <div v-if="isDropdownOpen"
                            class="absolute right-0 top-full min-w-36 bg-setting-bg rounded-lg shadow-lg border border-gray-700 z-50"
                            style="margin-top: 2px">
                            <div class="p-2">
                                <button
                                    class="menu-coin hidden w-full whitespace-nowrap px-4 py-2 text-left text-sm text-white hover:text-text-active transition-colors duration-200 items-center gap-2"
                                    @click="emit('coin-click')">
                                    <img src="~/assets/images/common/coin.webp" alt="credit" class="w-4 h-4" />
                                    <span>
                                        {{ balance }}
                                    </span>
                                </button>
                                <button
                                    class="w-full whitespace-nowrap px-4 py-2 text-left text-sm text-white hover:text-text-active transition-colors duration-200 flex items-center gap-2"
                                    @click.stop="emit('menu', 'profile')">
                                    <el-icon class="!text-base">
                                        <User />
                                    </el-icon>
                                    {{ t('menus.profile') }}
                                </button>
                                <button
                                    class="w-full whitespace-nowrap px-4 py-2 text-left text-sm text-white hover:text-text-active transition-colors duration-200 flex items-center gap-2"
                                    @click="emit('menu', 'billing')">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                        fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                        stroke-linejoin="round" class="h-4 w-4" aria-hidden="true">
                                        <rect width="20" height="14" x="2" y="5" rx="2"></rect>
                                        <line x1="2" x2="22" y1="10" y2="10"></line>
                                    </svg>
                                    {{ t('menus.billing') }}
                                </button>
                                <button
                                    class="w-full whitespace-nowrap px-4 py-2 text-left text-sm text-red-500 hover:text-text-active transition-colors duration-200 flex items-center gap-2"
                                    @click="emit('menu', 'logout')">
                                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1">
                                        </path>
                                    </svg>
                                    <span>
                                        <span>{{ t('menus.logout') }}</span>
                                        <span class="ml-1">(v{{ version }})</span>
                                    </span>
                                </button>
                            </div>
                        </div>
                    </Transition>
                </div>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { User } from '@element-plus/icons-vue'
import { useI18n } from '@/composables/useI18n'
import { availableLanguages } from '@/utils/common'
import { showLoginModal } from '@/utils/emitter'
import { LOGIN_ENTRY_ENABLED } from '@/utils/feature-flags'

type UserInfoLike = {
    avatar?: string
    nick_name?: string
    [k: string]: any
}

defineProps<{
    isLoggedIn: boolean
    balance: number
    userInfo: UserInfoLike | null
    version: string
}>()

const emit = defineEmits<{
    (e: 'coin-click'): void
    (e: 'menu', action: 'profile' | 'billing' | 'logout'): void
}>()

const { t, locale, changeLanguage } = useI18n()

// 顶部用户信息下拉（桌面端使用）
const isDropdownOpen = ref(false)
const dropdownRef = ref<HTMLElement | null>(null)
let closeTimer: ReturnType<typeof setTimeout> | null = null

const openDropdown = () => {
    if (closeTimer) {
        clearTimeout(closeTimer)
        closeTimer = null
    }
    isDropdownOpen.value = true
}

const closeDropdown = () => {
    closeTimer = setTimeout(() => {
        isDropdownOpen.value = false
        closeTimer = null
    }, 200)
}

const getCurrentLanguageName = () => {
    const currentLang = availableLanguages.find(
        lang => lang.code === locale.value
    )
    return currentLang ? currentLang.name : 'English'
}

const handleLanguageChange = (langCode: string) => {
    changeLanguage(langCode)
}
</script>

<style lang="less" scoped>
.nav-actions {
    display: flex;
    align-items: center;
}

.login-btn-custom {
    background-color: #99f768;
    color: #000;
    padding: 6px 20px;
    border-radius: 8px;
    font-family: 'Roboto', sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;
}

.coin {
    user-select: none;
    cursor: pointer;
}

/* 极小屏幕优化：保证关闭按钮不被挤压（< 430px） */
@media (max-width: 430px) {
    .nav-actions {
        gap: 0.25rem;
    }

    /* 隐藏语言下拉按钮，减小右侧占位 */
    .language-button {
        display: none;
    }

    .coin {
        display: none;
    }

    .menu-coin {
        display: flex;
    }
}
</style>
